import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Upload, ChevronRight, ChevronLeft, ShieldCheck, X, Info, Mail, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Tooltip, TooltipTrigger, TooltipContent, TooltipProvider,
} from "@/components/ui/tooltip";

const COMPLAINT_CATS = [
  "Non-payment", "Partial payment", "Delayed work", "Poor service", "Incomplete work", "Repeated excuses",
  "Rude behaviour", "Overcharging", "Hidden charges", "Fake product allegation", "Warranty refusal",
  "Refund delay", "Misrepresentation", "Wrong product", "Defective product", "Poor workmanship",
  "Unprofessional conduct", "Contract disagreement", "Property damage", "Harassment", "False commitment",
  "Failure to deliver", "Other",
];
const EVIDENCE_TYPES = ["Invoice", "Receipt", "Bank/payment proof", "Quotation", "Contract", "Warranty document", "Photos", "Videos", "Email", "WhatsApp screenshots", "Service report", "Delivery proof", "Other"];
const RELATIONSHIPS = ["Customer", "Vendor", "Contractor", "Freelancer", "Employer", "Employee", "Other"];
const STEPS = ["Party", "Details", "Categories", "Evidence", "AI Draft", "Declaration"];

const Hint = ({ text }) => (
  <Tooltip>
    <TooltipTrigger asChild><span className="inline-flex align-middle"><Info className="h-3.5 w-3.5 text-slate-400 ml-1 cursor-help" /></span></TooltipTrigger>
    <TooltipContent className="max-w-xs text-xs">{text}</TooltipContent>
  </Tooltip>
);

export default function FileComplaint() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const asRole = sp.get("as") || "customer";
  const partyType = asRole === "vendor" ? "about_customer" : "about_vendor";

  const [step, setStep] = useState(0);
  const [serviceCats, setServiceCats] = useState([]);
  const [party, setParty] = useState({ name: "", phone: "", email: "", city: "", locality: "", category: "", relationship: asRole === "vendor" ? "Customer" : "Vendor" });
  const [details, setDetails] = useState({
    transaction_date: "", invoice_number: "", amount_involved: "", amount_paid: "",
    promised_date: "", actual_date: "", description: "", previous_communication: "", desired_resolution: "",
    is_ongoing: false, safety_risk: false,
  });
  const [cats, setCats] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [draft, setDraft] = useState(null);
  const [drafting, setDrafting] = useState(false);
  const [declared, setDeclared] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);

  // complainant email verification
  const [email, setEmail] = useState(user?.email || "");
  const [emailVerified, setEmailVerified] = useState(!!user?.email_verified);
  const [emailOtp, setEmailOtp] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [emailDemo, setEmailDemo] = useState("");

  const pending = Math.max(0, (parseFloat(details.amount_involved) || 0) - (parseFloat(details.amount_paid) || 0));

  useEffect(() => {
    api.get(`/categories?type=${asRole === "vendor" ? "vendor" : "customer"}`).then((r) => setServiceCats(r.data)).catch(() => {});
  }, [asRole]);

  const setD = (k) => (e) => setDetails({ ...details, [k]: e.target.value });
  const toggleCat = (c) => setCats((p) => p.includes(c) ? p.filter((x) => x !== c) : [...p, c]);
  const addEvidence = () => setEvidence((p) => [...p, { name: `document-${p.length + 1}`, type: "Invoice", note: "" }]);

  const sendEmailCode = async () => {
    if (!email || !email.includes("@")) return toast.error("Enter a valid email");
    try {
      const r = await api.post("/email/otp/start", { email });
      setEmailSent(true); setEmailDemo(r.data.demo_code || "");
      toast.success("Verification code sent to your email");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send code"); }
  };
  const verifyEmailCode = async () => {
    try {
      await api.post("/email/otp/verify", { email, otp: emailOtp });
      setEmailVerified(true); setEmailSent(false);
      await refresh();
      toast.success("Email verified");
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid code"); }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) { toast.error("File too large. Files more than 500KB are not permissible."); e.target.value = ""; return; }
    setUploading(true); setUploadPct(0);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (ev) => { if (ev.total) setUploadPct(Math.round((ev.loaded / ev.total) * 100)); },
      });
      setEvidence((p) => [...p, { id: res.data.id, name: res.data.name, storage_path: res.data.storage_path, type: "Invoice", note: "" }]);
      toast.success("File uploaded securely");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    setUploading(false); setUploadPct(0); e.target.value = "";
  };

  const goNext = () => {
    if (step === 1) {
      if (!details.transaction_date) return toast.error("Date of transaction/service is required");
      if (!details.invoice_number.trim()) return toast.error("Invoice / order number is required");
      if (!details.description.trim()) return toast.error("Description of the issue is required");
      if (!emailVerified) return toast.error("Please verify your email to continue");
    }
    setStep(step + 1);
  };

  const genDraft = async () => {
    if (!details.description) return toast.error("Please describe the issue first (Details step)");
    setDrafting(true);
    try {
      const res = await api.post("/ai/draft-complaint", {
        party_type: partyType, party_name: party.name || null, description: details.description,
        amount_pending: pending || null, desired_resolution: details.desired_resolution, categories: cats,
      });
      setDraft(res.data);
      toast.success("AI draft generated");
    } catch (e) { toast.error(e.response?.data?.detail || "AI drafting failed"); }
    setDrafting(false);
  };

  const submit = async () => {
    if (!declared) return toast.error("Please accept the declaration");
    if (!details.transaction_date || !details.invoice_number.trim() || !details.description.trim()) return toast.error("Invoice number, date and description are required");
    if (!emailVerified) return toast.error("Please verify your email first");
    if (!party.name || !party.phone) return toast.error("Enter the other party's name and mobile number");
    setSubmitting(true);
    try {
      const res = await api.post("/complaints", {
        party_type: partyType,
        party: { ...party, category: party.category || null },
        transaction_date: details.transaction_date, invoice_number: details.invoice_number,
        amount_involved: parseFloat(details.amount_involved) || null,
        amount_paid: parseFloat(details.amount_paid) || null,
        amount_pending: pending || null,
        promised_date: details.promised_date || null, actual_date: details.actual_date || null,
        description: details.description, previous_communication: details.previous_communication || null,
        desired_resolution: details.desired_resolution || null,
        is_ongoing: details.is_ongoing, safety_risk: details.safety_risk,
        categories: cats, evidence, drafted_text: draft?.formal_letter || null,
      });
      toast.success(`Complaint ${res.data.complaint_code} submitted`);
      navigate(`/complaint/${res.data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Submission failed"); }
    setSubmitting(false);
  };

  return (
    <TooltipProvider delayDuration={150}>
    <div className="ca-container py-10 max-w-3xl">
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">
        {asRole === "vendor" ? "Complaint about a Customer" : "Complaint about a Vendor"}
      </h1>
      <div className="mt-5 flex items-center gap-1 overflow-x-auto pb-2">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center shrink-0">
            <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold ${i <= step ? "bg-teal-brand text-white" : "bg-slate-200 text-slate-500"}`}>{i + 1}</div>
            <span className={`ml-1.5 text-xs ${i === step ? "text-slate-900 font-semibold" : "text-slate-400"}`}>{s}</span>
            {i < STEPS.length - 1 && <div className="w-5 h-px bg-slate-200 mx-1" />}
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6">
        {step === 0 && (
          <div className="space-y-4">
            <p className="text-sm text-slate-500">Enter the other party's information. Where possible we verify the mobile number; unverified numbers restrict public visibility.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Name / Business name <Hint text="The exact name of the person or business you are complaining about." /></Label><Input data-testid="party-name" value={party.name} onChange={(e) => setParty({ ...party, name: e.target.value })} placeholder="e.g. Sharma Plumbing Works" /></div>
              <div><Label>Mobile number <Hint text="Their mobile number with country code. We mask it publicly and never show it in full." /></Label><Input data-testid="party-phone" value={party.phone} onChange={(e) => setParty({ ...party, phone: e.target.value })} placeholder="+91 98765 43210" /></div>
              <div><Label>Email (optional)</Label><Input data-testid="party-email" value={party.email} onChange={(e) => setParty({ ...party, email: e.target.value })} placeholder="name@example.com" /></div>
              <div><Label>City <Hint text="City where the service or transaction took place. Helps others searching locally." /></Label><Input data-testid="party-city" value={party.city} onChange={(e) => setParty({ ...party, city: e.target.value })} placeholder="e.g. Pune" /></div>
              <div><Label>Locality <Hint text="Area or neighbourhood within the city (optional)." /></Label><Input data-testid="party-locality" value={party.locality} onChange={(e) => setParty({ ...party, locality: e.target.value })} placeholder="e.g. Kothrud" /></div>
              <div>
                <Label>Service / transaction category <Hint text="The type of service or product involved (e.g. plumbing, electronics). Helps categorise the dispute." /></Label>
                <Select value={party.category} onValueChange={(v) => setParty({ ...party, category: v })}>
                  <SelectTrigger data-testid="party-category"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="max-h-64">{serviceCats.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Relationship <Hint text="How you relate to the other party (customer, vendor, contractor, etc.)." /></Label>
                <Select value={party.relationship} onValueChange={(v) => setParty({ ...party, relationship: v })}>
                  <SelectTrigger data-testid="party-relationship"><SelectValue /></SelectTrigger>
                  <SelectContent>{RELATIONSHIPS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            {/* Email verification */}
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <Label>Your email {emailVerified ? <span className="text-emerald-600 text-xs inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> verified</span> : <span className="text-rose-500">* required &amp; must be verified</span>} <Hint text="We send case updates here. It must be verified with a one-time code so notifications reach you." /></Label>
              <div className="flex gap-2">
                <Input value={email} onChange={(e) => { setEmail(e.target.value); setEmailVerified(false); }} data-testid="filer-email" placeholder="you@example.com" disabled={emailVerified} />
                {!emailVerified && <Button variant="outline" onClick={sendEmailCode} data-testid="filer-send-code"><Mail className="h-4 w-4 mr-1" /> Send code</Button>}
              </div>
              {emailSent && !emailVerified && (
                <div className="mt-2 flex gap-2 items-center flex-wrap">
                  {emailDemo && <span className="text-xs text-amber-700">Demo code: <b className="font-mono">{emailDemo}</b></span>}
                  <Input value={emailOtp} onChange={(e) => setEmailOtp(e.target.value)} placeholder="6-digit code" className="w-40" data-testid="filer-email-otp" />
                  <Button onClick={verifyEmailCode} className="bg-teal-brand text-white" data-testid="filer-verify-code">Verify</Button>
                </div>
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Date of transaction/service <span className="text-rose-500">*</span> <Hint text="When the service or transaction happened. Required." /></Label><Input type="date" data-testid="d-transaction-date" value={details.transaction_date} onChange={setD("transaction_date")} /></div>
              <div><Label>Invoice / order number <span className="text-rose-500">*</span> <Hint text="The bill, invoice or order reference. Required — helps identify the transaction." /></Label><Input data-testid="d-invoice" value={details.invoice_number} onChange={setD("invoice_number")} placeholder="e.g. INV-2043" /></div>
              <div><Label>Amount involved (₹) <Hint text="Total agreed amount for the service or product." /></Label><Input type="number" data-testid="d-amount" value={details.amount_involved} onChange={setD("amount_involved")} placeholder="e.g. 12000" /></div>
              <div><Label>Amount paid (₹) <Hint text="How much you have already paid so far." /></Label><Input type="number" data-testid="d-paid" value={details.amount_paid} onChange={setD("amount_paid")} placeholder="e.g. 6000" /></div>
              <div><Label>Amount pending (₹) <Hint text="Auto-calculated as amount involved minus amount paid." /></Label><Input type="number" data-testid="d-pending" value={pending} readOnly className="bg-slate-100" /></div>
              <div><Label>Promised completion date <Hint text="The date the work/delivery was promised to be finished." /></Label><Input type="date" data-testid="d-promised" value={details.promised_date} onChange={setD("promised_date")} /></div>
              <div><Label>Actual completion date <Hint text="When it was actually completed, if at all. Leave blank if still pending." /></Label><Input type="date" data-testid="d-actual" value={details.actual_date} onChange={setD("actual_date")} /></div>
              <div className="sm:col-span-2"><Label>Description of the issue <span className="text-rose-500">*</span> <Hint text="Stick to facts: what was agreed, what happened, and dates. Avoid abusive language." /></Label><Textarea rows={4} data-testid="d-description" value={details.description} onChange={setD("description")} placeholder="e.g. Booked bathroom pipeline repair on 3 Jan for ₹12,000. Work stopped after ₹6,000 advance and was not completed despite reminders." /></div>
              <div className="sm:col-span-2"><Label>Previous communication <Hint text="Summarise earlier attempts to resolve this — calls, messages, dates and what was said." /></Label><Textarea rows={2} data-testid="d-prev-comm" value={details.previous_communication} onChange={setD("previous_communication")} placeholder="e.g. Called on 10 Jan and 15 Jan; promised to return but did not." /></div>
              <div className="sm:col-span-2"><Label>Desired resolution <Hint text="What outcome would resolve this for you?" /></Label><Textarea rows={2} data-testid="d-resolution" value={details.desired_resolution} onChange={setD("desired_resolution")} placeholder="e.g. Complete the pending work within 7 days or refund ₹6,000." /></div>
              <label className="flex items-center gap-2 text-sm text-slate-600"><Checkbox checked={details.is_ongoing} onCheckedChange={(v) => setDetails({ ...details, is_ongoing: v })} data-testid="d-ongoing" /> Issue is ongoing</label>
              <label className="flex items-center gap-2 text-sm text-slate-600"><Checkbox checked={details.safety_risk} onCheckedChange={(v) => setDetails({ ...details, safety_risk: v })} data-testid="d-safety" /> Immediate safety risk</label>
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <p className="text-sm text-slate-500 mb-3">Select all categories that apply.</p>
            <div className="flex flex-wrap gap-2">
              {COMPLAINT_CATS.map((c) => (
                <button key={c} onClick={() => toggleCat(c)} data-testid={`cat-${c}`}
                  className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${cats.includes(c) ? "bg-teal-brand text-white border-teal-brand" : "bg-slate-50 border-slate-200 text-slate-700 hover:border-teal-brand/40"}`}>
                  {c}
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800 mb-4">
              Redaction warning: Remove Aadhaar, PAN, bank details, passwords and other sensitive information before uploading. Evidence is stored privately with an integrity record and timestamp.
            </div>
            <div className="space-y-3">
              {evidence.map((ev, i) => (
                <div key={i} className="rounded-xl border border-slate-200 p-3 grid sm:grid-cols-3 gap-2 items-end">
                  <div><Label className="text-xs">File name</Label><Input value={ev.name} onChange={(e) => setEvidence(evidence.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} /></div>
                  <div>
                    <Label className="text-xs">Type</Label>
                    <Select value={ev.type} onValueChange={(v) => setEvidence(evidence.map((x, j) => j === i ? { ...x, type: v } : x))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{EVIDENCE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2">
                    <Input placeholder="Description" value={ev.note} onChange={(e) => setEvidence(evidence.map((x, j) => j === i ? { ...x, note: e.target.value } : x))} />
                    <Button variant="ghost" size="icon" onClick={() => setEvidence(evidence.filter((_, j) => j !== i))}><X className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3 mt-3 items-center">
              <label className="inline-flex items-center gap-2 rounded-lg border border-teal-brand bg-teal-brand/5 text-teal font-medium text-sm px-4 py-2 cursor-pointer hover:bg-teal-brand/10" data-testid="upload-evidence-label">
                <Upload className="h-4 w-4" /> {uploading ? `Uploading… ${uploadPct}%` : "Upload file (private)"}
                <input type="file" hidden onChange={handleFile} disabled={uploading} data-testid="evidence-file-input" accept=".jpg,.jpeg,.png,.webp,.gif,.pdf,.doc,.docx,.txt,.csv" />
              </label>
              <Button variant="outline" onClick={addEvidence} data-testid="add-evidence-btn">Add manual entry</Button>
              <span className="text-xs text-slate-400">Maximum file size 500KB. Files more than 500KB are not permissible.</span>
            </div>
            {uploading && (
              <div className="mt-3 h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full bg-teal-brand transition-all" style={{ width: `${uploadPct}%` }} data-testid="upload-progress" />
              </div>
            )}
          </div>
        )}

        {step === 4 && (
          <div>
            <p className="text-sm text-slate-500 mb-3">Generate a professional, factual message addressed directly to {party.name || "the other party"}. It removes abusive language, separates facts from assumptions, and states a reasonable resolution.</p>
            <Button onClick={genDraft} disabled={drafting} className="bg-teal-brand hover:bg-teal-700 text-white" data-testid="ai-draft-btn">
              <Sparkles className="h-4 w-4 mr-2" /> {drafting ? "Drafting…" : "Generate AI Draft"}
            </Button>
            {draft && (
              <div className="mt-4 space-y-3">
                {[["Formal letter", draft.formal_letter], ["Email version", draft.email_version], ["WhatsApp version", draft.whatsapp_version]].map(([t, v]) => (
                  <div key={t} className="rounded-xl border border-slate-200 p-4">
                    <p className="text-xs font-semibold text-teal uppercase tracking-wide mb-2">{t}</p>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{v}</p>
                  </div>
                ))}
                {draft.missing_evidence?.length > 0 && (
                  <div className="rounded-xl bg-blue-50 border border-blue-200 p-3 text-sm text-blue-800">
                    <p className="font-semibold">Suggested additional evidence:</p>
                    <ul className="list-disc pl-4 mt-1">{draft.missing_evidence.map((m, i) => <li key={i}>{m}</li>)}</ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {step === 5 && (
          <div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
              "I confirm that the information submitted is accurate to the best of my knowledge. I understand that this
              complaint is an allegation and that the other party may respond. I will not use this platform for threats,
              harassment, false claims, or disclosure of private information."
            </div>
            <label className="flex items-center gap-2 mt-4 text-sm text-slate-800">
              <Checkbox checked={declared} onCheckedChange={setDeclared} data-testid="declaration-check" />
              I have read and accept this declaration.
            </label>
          </div>
        )}
      </div>

      <div className="mt-5 flex justify-between">
        <Button variant="outline" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="step-prev"><ChevronLeft className="h-4 w-4 mr-1" /> Back</Button>
        {step < STEPS.length - 1 ? (
          <Button className="bg-teal-brand hover:bg-teal-700 text-white" onClick={goNext} data-testid="step-next">Next <ChevronRight className="h-4 w-4 ml-1" /></Button>
        ) : (
          <Button className="bg-teal-brand hover:bg-teal-700 text-white" disabled={submitting} onClick={submit} data-testid="submit-complaint-btn">
            <ShieldCheck className="h-4 w-4 mr-2" /> {submitting ? "Submitting…" : "Submit Complaint"}
          </Button>
        )}
      </div>
    </div>
    </TooltipProvider>
  );
}
